import { ApiError, type Problem } from './api';
import {
  getDefaultAdminSessionStore,
  readWebInstallationId,
  type AdminSession,
  type AdminSessionStore,
  type AdminUser,
} from './admin-session';
import { composeApiRequestUrl } from './config/api-base-url';
import { readJson } from './http';

export function adminRequestHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

interface TokenPairBody {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly auth_context?: 'NORMAL' | 'PASSWORD_CHANGE_RESTRICTED';
  readonly user?: AdminUser | null;
}

export interface UserSummary {
  readonly id: string;
  readonly username: string;
  readonly display_name: string;
  readonly status: string;
  readonly role_codes: readonly string[];
  readonly must_change_password?: boolean;
  readonly version: string;
}

export interface TemporaryPasswordIssued {
  readonly user: UserSummary;
  readonly temporary_password: string;
  readonly must_change_password: true;
}

export interface ContractorProfile {
  readonly id: string;
  readonly legal_name: string;
  readonly inn: string | null;
  readonly aliases: readonly string[];
  readonly status: string;
  readonly version: string;
  readonly attributes: Record<string, unknown>;
}

export interface VehicleLastSignedDecision {
  readonly label: 'Новый' | 'Допущен' | 'Не допущен';
  readonly decision: 'APPROVED' | 'REJECTED' | null;
  readonly decided_at: string | null;
  readonly case_id: string | null;
  readonly case_number: string | null;
}

export interface VehicleProfile {
  readonly id: string;
  readonly registration_plate: string | null;
  readonly vin: string | null;
  readonly alternative_identifier: string | null;
  readonly status: string;
  readonly version: string;
  readonly attributes: Record<string, unknown>;
  readonly last_signed_decision?: VehicleLastSignedDecision;
}

export interface ContractorVehicleLink {
  readonly id: string;
  readonly contractor_profile_id: string;
  readonly vehicle_profile_id: string;
  readonly relationship: 'USED_BY';
  readonly status: 'ACTIVE' | 'ARCHIVED';
  readonly first_seen_at: string;
  readonly last_seen_at: string;
  readonly version: string;
}

export interface ChecklistTemplateAdmin {
  readonly id: string;
  readonly template_code: string;
  readonly name: string;
  readonly active_template_version_id: string | null;
  readonly has_approved_version: boolean;
  readonly active_version_code: string | null;
  readonly version: string;
  readonly is_deleted?: boolean;
  readonly deleted_at?: string | null;
  readonly deleted_by_user_id?: string | null;
  readonly last_active_template_version_id_before_delete?: string | null;
}

export interface TemplateImportProfile {
  readonly id: string;
  readonly code: string;
  readonly source_family: string;
  readonly status: string;
  readonly current_revision_id: string | null;
  readonly version: string;
}

export interface TemplateSourceArtifact {
  readonly id: string;
  readonly original_filename: string;
  readonly media_type: string;
  readonly byte_size: number;
  readonly sha256: string;
  readonly created_at: string;
  readonly discovered_sheets: readonly string[];
}

export interface TemplateImportRun {
  readonly id: string;
  readonly source_artifact_id: string;
  readonly source_sheet: string;
  readonly import_profile_revision_id: string;
  readonly checklist_template_id: string;
  readonly status: string;
  readonly staging: Record<string, unknown> | null;
  readonly published_template_version_id: string | null;
  readonly published_version_code: string | null;
  readonly validation_report: {
    readonly validated_at: string;
    readonly error_count: number;
    readonly warning_count: number;
    readonly issues: readonly {
      readonly severity: string;
      readonly code: string;
      readonly message: string;
      readonly field_key?: string | null;
      readonly item_code?: string | null;
      readonly source_locator?: Record<string, unknown> | null;
    }[];
  } | null;
  readonly version: string;
}

export interface InspectorQualification {
  readonly id: string;
  readonly user_id: string;
  readonly checklist_template_id: string;
  readonly status: string;
  readonly version: string;
}

export interface DuplicateCandidate<T> {
  readonly profile: T;
  readonly matched_fields: readonly string[];
}

export interface ReferenceDatasetSummary {
  readonly dataset_key: string;
  readonly display_name: string;
  readonly version: string;
  readonly payload_checksum: string;
  readonly updated_at: string;
}

export interface ReferenceDataset {
  readonly dataset_key: string;
  readonly display_name: string;
  readonly payload: Record<string, unknown>;
  readonly payload_checksum: string;
  readonly version: string;
  readonly updated_at: string;
}

export interface EmailRecipientPolicy {
  readonly id: string;
  readonly status: 'ACTIVE' | 'INACTIVE';
  readonly scope_kind: 'RULE' | 'GLOBAL';
  readonly template_id: string | null;
  readonly reference_scope: Record<string, unknown> | null;
  readonly contractor_organization_profile_id: string | null;
  readonly priority: number;
  readonly recipients: readonly string[];
  readonly version: string;
}

export interface EmailRecipientResolution {
  readonly policy_id: string;
  readonly scope_kind: 'RULE' | 'GLOBAL';
  readonly priority: number;
  readonly recipients: readonly string[];
}

export interface EmailCaseRecipientPreview {
  readonly inspection_case_id: string;
  readonly case_number: string;
  readonly booking_source: string;
  readonly primary: {
    readonly email: string | null;
    readonly provenance: string | null;
    readonly unavailable_reason: string | null;
    readonly source_label: string;
  };
  readonly additional_recipients: readonly {
    readonly email: string;
    readonly source: string;
    readonly policy_id: string | null;
    readonly scope_kind: string | null;
  }[];
  readonly resolved_recipients: readonly string[];
  readonly policy_error: string | null;
  readonly deliveries: readonly {
    readonly id: string;
    readonly status: string;
    readonly attempt_count: number;
    readonly sent_at: string | null;
    readonly last_error: string | null;
    readonly recipient_count: number;
    readonly reliability_note: string | null;
  }[];
}

export interface RubitimeBusinessConfig {
  readonly branch_id: string;
  readonly service_id: string;
  readonly cooperator_id: string;
  readonly version: string;
}

export interface AdminOpsSummary {
  readonly open_cases: number;
  readonly pending_exceptions: number;
  readonly unpublished_import_runs: number;
}

export interface AdminAuditEvent {
  readonly id: string;
  readonly inspection_case_id: string | null;
  readonly event_type: string;
  readonly actor_type: string;
  readonly actor_user_id: string | null;
  readonly subject_type: string;
  readonly subject_id: string | null;
  readonly occurred_at: string;
  readonly payload: Record<string, unknown>;
}

export interface InspectionBooking {
  readonly id: string;
  readonly rubitime_record_id: string;
  readonly status: string;
  readonly scheduled_start_at: string;
  readonly scheduled_end_at?: string;
  readonly version: string;
}

export interface InspectionAssignment {
  readonly id?: string;
  readonly user_id: string;
  readonly inspection_role_code: string;
  readonly checklist_instance_id: string | null;
  readonly assigned_at?: string;
  readonly unassigned_at: string | null;
}

export interface InspectionCaseSummary {
  readonly id: string;
  readonly case_number: string;
  readonly status: string;
  readonly version: string;
  readonly booking: InspectionBooking;
  readonly contractor_edit_cutoff_at: string;
  readonly assignments: readonly InspectionAssignment[];
  readonly vehicle?: {
    readonly registration_number?: string | null;
    readonly vin_or_alternative_identifier?: string | null;
  };
  readonly contractor?: Record<string, unknown>;
}

export interface ArchivedInspectionCaseSummary extends InspectionCaseSummary {
  readonly decision: 'APPROVED' | 'REJECTED' | null;
}

export interface ChecklistInstanceSummary {
  readonly id: string;
  readonly template_version_id: string;
  readonly status?: string;
  readonly version?: string;
}

export interface InspectionCaseDetail extends InspectionCaseSummary {
  readonly vehicle_snapshot?: Record<string, unknown>;
  readonly contractor_snapshot?: Record<string, unknown>;
  readonly started_at?: string | null;
  readonly completed_at?: string | null;
  readonly final_comment?: string | null;
  readonly checklist_instances: readonly ChecklistInstanceSummary[];
  readonly decision?: InspectionDecision | null;
  readonly result_version_id?: string | null;
  readonly signature_record_id?: string | null;
  readonly document_artifact_id?: string | null;
}

export interface ChecklistInstanceDetail {
  readonly id: string;
  readonly inspection_case_id: string;
  readonly template_version_id: string;
  readonly status: string;
  readonly version: string;
  readonly completed_at?: string | null;
  readonly frozen_at?: string | null;
}

export interface TemplateVersionSummary {
  readonly id: string;
  readonly template_id: string;
  readonly template_code: string;
  readonly version_code: string;
}

/** Exact published TemplateVersionDto (GET /template-versions/{id}). */
export interface PublishedTemplateVersionDetail {
  readonly id: string;
  readonly template_id: string;
  readonly template_code: string;
  readonly version_code: string;
  readonly definition_checksum: string;
  readonly source_file?: string | null;
  readonly source_sheet?: string | null;
  readonly source_checksum?: string | null;
  readonly authoring_provenance?:
    | TemplateAuthoringProvenance
    | string
    | {
        readonly kind?: string;
        readonly import_run_id?: string | null;
        readonly source_artifact_id?: string | null;
        readonly source_template_version_id?: string | null;
      };
  readonly presentation_schema_version?: number;
  readonly presentation?: Record<string, unknown>;
  readonly presentation_checksum?: string;
  readonly sections: readonly {
    readonly section_code: string;
    readonly ordinal: number;
    readonly title: string;
    readonly metadata?: Record<string, unknown>;
  }[];
  readonly items: readonly {
    readonly item_code: string;
    readonly section_code: string;
    readonly ordinal: number;
    readonly label: string;
    readonly owner_code: string;
    readonly required_rule: Record<string, unknown>;
    readonly applicability_rule: Record<string, unknown>;
    readonly validation: Record<string, unknown>;
    readonly normative_reference?: string | null;
    readonly pdf_metadata?: Record<string, unknown>;
  }[];
  readonly checklist_fields?: readonly {
    readonly id: string;
    readonly template_version_id: string;
    readonly field_key: string;
    readonly binding_kind: string;
    readonly label: string;
    readonly value_type: string;
    readonly owner_code: string;
    readonly required_rule: Record<string, unknown>;
    readonly applicability_rule: Record<string, unknown>;
    readonly validation: Record<string, unknown>;
    readonly placement: string;
    readonly ordinal?: number;
    readonly pdf_metadata?: Record<string, unknown>;
    readonly snapshot_path?: string | null;
    readonly semantic_key?: string | null;
    readonly master_object_role?: string | null;
    readonly master_attribute?: string | null;
  }[];
  readonly document_requirements?: readonly {
    readonly id: string;
    readonly template_version_id: string;
    readonly document_type_id: string;
    readonly document_type_code: string;
    readonly display_label: string;
    readonly subject_binding: string;
    readonly capture_mode: string;
    readonly required_rule: Record<string, unknown>;
    readonly applicability_rule: Record<string, unknown>;
    readonly verification_metadata?: Record<string, unknown>;
    readonly ordinal: number;
    readonly metadata_fields: readonly {
      readonly id: string;
      readonly field_code: string;
      readonly label: string;
      readonly value_type: string;
      readonly required: boolean;
      readonly validation?: Record<string, unknown>;
      readonly ordinal: number;
    }[];
  }[];
}

export type TemplateAuthoringProvenance = 'MANUAL' | 'IMPORTED' | 'CLONED';
export type TemplateAuthoringDraftStatus = 'DRAFT' | 'VALIDATED' | 'PUBLISHED' | 'DISCARDED';
export type TemplateAuthoringPreviewView = 'CONTRACTOR' | 'INSPECTOR' | 'PDF';

export interface TemplateFieldReuseUsedIn {
  readonly checklist_template_id: string;
  readonly template_code: string;
  readonly template_name: string;
}

export type TemplateFieldReuseConflictReasonCode =
  | 'OWNER_CODE_MISMATCH'
  | 'VALUE_TYPE_MISMATCH'
  | 'REQUIRED_RULE_MISMATCH'
  | 'APPLICABILITY_RULE_MISMATCH'
  | 'VALIDATION_MISMATCH';

/** Read-only CASE_DYNAMIC reuse catalog item (ACTIVE versions). Not wired to UI yet. */
export interface TemplateFieldReuseCandidate {
  readonly binding_kind: 'CASE_DYNAMIC';
  readonly semantic_key: string;
  readonly label: string;
  readonly value_type: string;
  readonly owner_code: string;
  readonly required_rule: Record<string, unknown>;
  readonly applicability_rule: Record<string, unknown>;
  readonly validation: Record<string, unknown>;
  readonly used_in: readonly TemplateFieldReuseUsedIn[];
  readonly linkable: boolean;
  readonly conflict_reason_codes: readonly TemplateFieldReuseConflictReasonCode[];
}

export interface TemplateAuthoringValidationIssue {
  readonly code: string;
  readonly severity: 'ERROR' | 'WARNING' | string;
  readonly subject_kind:
    'GENERAL' | 'FIELD' | 'DOCUMENT' | 'SECTION' | 'ITEM' | 'PRESENTATION' | string;
  readonly subject_id: string | null;
  readonly message: string;
  readonly source_lineage?: Record<string, unknown>;
}

export interface TemplateAuthoringValidationReport {
  readonly draft_id: string;
  readonly draft_version: string;
  readonly status: TemplateAuthoringDraftStatus;
  readonly errors: readonly TemplateAuthoringValidationIssue[];
  readonly warnings: readonly TemplateAuthoringValidationIssue[];
  readonly validated_at: string;
}

export interface TemplateAuthoringDraftSummary {
  readonly id: string;
  readonly checklist_template_id: string;
  readonly template_code: string;
  readonly template_name: string;
  readonly provenance: TemplateAuthoringProvenance;
  readonly status: TemplateAuthoringDraftStatus;
  readonly version: string;
  readonly updated_at: string;
  readonly published_template_version_id: string | null;
}

export interface TemplateAuthoringDraft {
  readonly id: string;
  readonly checklist_template_id: string;
  readonly provenance: TemplateAuthoringProvenance;
  readonly status: TemplateAuthoringDraftStatus;
  readonly authoring_schema_version: number;
  readonly authoring_payload: Record<string, unknown>;
  readonly validation_report: TemplateAuthoringValidationReport | null;
  readonly version: string;
  readonly created_by_user_id: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly import_run_id?: string | null;
  readonly cloned_from_template_version_id?: string | null;
  readonly published_template_version_id?: string | null;
  readonly validated_at?: string | null;
  readonly document_requirements_draft?: readonly {
    readonly document_type_id: string;
    readonly capture_mode: string;
    readonly display_label?: string;
  }[];
}

export interface TemplateAuthoringPreview {
  readonly draft_id: string;
  readonly draft_version: string;
  readonly view: TemplateAuthoringPreviewView;
  readonly resolved_schema: Record<string, unknown>;
  readonly warnings: readonly TemplateAuthoringValidationIssue[];
}

export interface DocumentTypeAdmin {
  readonly id: string;
  readonly code: string;
  readonly display_name: string;
  readonly status: string;
  readonly default_metadata_schema: Record<string, unknown>;
  readonly version: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly description?: string | null;
}

export type ConfigurationImpactMode = 'STANDARD' | 'MANDATORY_PRESTART';

export interface EquipmentTypeRuntimeReadiness {
  readonly selectable: boolean;
  readonly problem_code?: string | null;
  readonly problem_details?: Record<string, unknown> | null;
}

export interface EquipmentTypeAdmin {
  readonly id: string;
  readonly code: string;
  readonly display_name: string;
  readonly status: string;
  readonly display_order: number;
  readonly current_package_revision_id: string | null;
  readonly runtime_readiness: EquipmentTypeRuntimeReadiness;
  readonly version: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly description?: string | null;
}

export interface EquipmentPackageRevisionItem {
  readonly checklist_template_id: string;
  readonly template_code: string;
  readonly template_name: string;
  readonly ordinal: number;
}

export interface EquipmentPackageRevision {
  readonly id: string;
  readonly equipment_type_id: string;
  readonly revision_number: number;
  readonly checksum: string;
  readonly items: readonly EquipmentPackageRevisionItem[];
  readonly created_by_user_id: string;
  readonly created_at: string;
  readonly is_current: boolean;
}

export interface ConfigurationImpactCounts {
  readonly new_cases: number;
  readonly draft_cases: number;
  readonly ready_not_started_cases: number;
  readonly in_progress_cases: number;
  readonly finalized_cases: number;
  readonly preloaded_cases: number;
  readonly unacknowledged_devices: number;
}

export interface ConfigurationImpactGroup {
  readonly group: string;
  readonly count: number;
  readonly action_required: boolean;
}

export interface ConfigurationImpactAssessment {
  readonly id: string;
  readonly target_kind: string;
  readonly target_root_id: string;
  readonly target_revision_or_version_id: string | null;
  readonly impact_mode: ConfigurationImpactMode | string;
  readonly expected_root_version: string;
  readonly counts: ConfigurationImpactCounts;
  readonly affected_groups: readonly ConfigurationImpactGroup[];
  readonly assessment_checksum: string;
  readonly created_at: string;
  readonly expires_at: string;
}

export interface InspectionSiteAdmin {
  readonly id: string;
  readonly code: string;
  readonly display_name: string;
  readonly status: string;
  readonly display_order: number;
  readonly version: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly description?: string | null;
}

export interface PublishedTemplateVersionResult {
  readonly id: string;
  readonly template_id: string;
  readonly template_code: string;
  readonly version_code: string;
  readonly definition_checksum?: string;
  readonly authoring_provenance?: TemplateAuthoringProvenance | string;
  readonly source_file?: string | null;
  readonly sections?: readonly unknown[];
  readonly items?: readonly unknown[];
  readonly status?: string;
  readonly is_active?: boolean;
}

export interface InspectionDecision {
  readonly id: string;
  readonly inspection_case_id: string;
  readonly decision: 'APPROVED' | 'REJECTED' | string;
  readonly decided_at: string;
}

export interface InspectionResultVersion {
  readonly id: string;
  readonly inspection_case_id: string;
  readonly inspection_decision_id: string;
  readonly completed_at: string;
  readonly business_revision: string;
  readonly payload_checksum: string;
  readonly payload_schema_version: number;
}

export interface SignatureRecord {
  readonly id: string;
  readonly inspection_result_version_id: string;
  readonly signer_user_id: string;
  readonly signer_role: string;
  readonly signed_at: string;
  readonly document_artifact_id: string;
}

export interface DocumentArtifact {
  readonly id: string;
  readonly inspection_case_id: string;
  readonly inspection_result_version_id: string;
  readonly generation_status: 'GENERATION_QUEUED' | 'GENERATED' | 'GENERATION_ERROR' | string;
  readonly version: string;
  readonly content_checksum?: string | null;
  readonly content_type?: string | null;
  readonly content_size?: number | null;
  readonly generated_at?: string | null;
  readonly last_error_code?: string | null;
  readonly last_error_message?: string | null;
}

export interface EmailDelivery {
  readonly id: string;
  readonly document_artifact_id: string;
  readonly recipient_addresses: readonly string[];
  readonly status: 'SEND_QUEUED' | 'SENT' | 'SEND_ERROR' | string;
  readonly attempt_count: number;
  readonly version: string;
  readonly last_attempt_at?: string | null;
  readonly sent_at?: string | null;
  readonly last_error?: string | null;
}

export interface ArchivedCaseBundle {
  readonly inspection_case: InspectionCaseDetail;
  readonly decision: InspectionDecision | null;
  readonly result_version: InspectionResultVersion | null;
  readonly signature_record: SignatureRecord | null;
  readonly document_artifact: DocumentArtifact | null;
  readonly email_deliveries: readonly EmailDelivery[];
}

export interface DocumentContent {
  readonly blob: Blob;
  readonly filename: string | null;
}

export interface AdminFacsimile {
  readonly id: string;
  readonly user_id: string;
  readonly current_version_id: string | null;
  readonly version: string;
  readonly versions: readonly {
    readonly id: string;
    readonly facsimile_id: string;
    readonly content_type: string;
    readonly content_size: number;
    readonly content_checksum: string;
    readonly created_by_user_id: string;
    readonly created_at: string;
  }[];
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function parseDocumentFilename(disposition: string | null): string | null {
  if (disposition === null || disposition.length === 0) {
    return null;
  }
  const star = disposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (star !== null && star[1] !== undefined) {
    const decoded = decodeRfc5987Filename(star[1].trim());
    if (decoded !== null) {
      return decoded;
    }
  }
  const plain = disposition.match(/(?:^|[;\s])filename\s*=\s*([^;]+)/i);
  if (plain === null || plain[1] === undefined) {
    return null;
  }
  const stripped = stripFilenameQuotes(plain[1].trim());
  return stripped.length === 0 ? null : stripped;
}

function decodeRfc5987Filename(value: string): string | null {
  const match = /^UTF-8''(.+)$/i.exec(value);
  if (match === null || match[1] === undefined) {
    return null;
  }
  try {
    const decoded = decodeURIComponent(match[1]);
    return decoded.length === 0 ? null : decoded;
  } catch {
    return null;
  }
}

function stripFilenameQuotes(value: string): string {
  if (
    (value.startsWith('"') && value.endsWith('"') && value.length >= 2) ||
    (value.startsWith("'") && value.endsWith("'") && value.length >= 2)
  ) {
    return value.slice(1, -1);
  }
  return value;
}

async function sessionFromAccessToken(
  request: (
    method: string,
    path: string,
    options?: {
      body?: unknown;
      accessToken?: string | null;
      idempotency?: boolean;
      form?: FormData;
    },
  ) => Promise<unknown>,
  accessToken: string,
  authContext: AdminSession['authContext'] | undefined,
): Promise<AdminSession> {
  const me = (await request('GET', '/auth/me', { accessToken })) as AdminUser;
  return {
    accessToken,
    user: me,
    authContext: authContext ?? 'NORMAL',
  };
}

export type AdminAccessTokenRecoverer = (
  failedAccessToken: string | null,
) => Promise<AdminSession | null>;

function isUnauthenticatedProblem(status: number, body: unknown): boolean {
  if (status !== 401) {
    return false;
  }
  if (typeof body !== 'object' || body === null) {
    return false;
  }
  return (body as Problem).code === 'UNAUTHENTICATED';
}

export function createAdminApi(
  baseUrl: string,
  deps: {
    readonly store?: AdminSessionStore;
    readonly recoverAccessToken?: AdminAccessTokenRecoverer;
  } = {},
) {
  const store = deps.store ?? getDefaultAdminSessionStore();
  let recoverAccessToken = deps.recoverAccessToken;

  const request = async (
    method: string,
    path: string,
    options: {
      body?: unknown;
      accessToken?: string | null;
      idempotency?: boolean;
      form?: FormData;
    } = {},
  ): Promise<unknown> => {
    const sessionBacked = options.accessToken === undefined;
    const idempotencyKey = options.idempotency === true ? newIdempotencyKey() : undefined;
    const requestBody =
      options.form !== undefined
        ? options.form
        : options.body === undefined
          ? undefined
          : JSON.stringify(options.body);

    const execute = async (accessToken: string | null): Promise<ReturnType<typeof readJson>> => {
      const headers = adminRequestHeaders(accessToken);
      if (idempotencyKey !== undefined) {
        headers['Idempotency-Key'] = idempotencyKey;
      }
      if (options.form === undefined && options.body !== undefined) {
        headers['Content-Type'] = 'application/json';
      }
      return readJson(composeApiRequestUrl(baseUrl, path), {
        method,
        credentials: 'include',
        headers,
        body: requestBody,
      });
    };

    const initialToken = sessionBacked
      ? (store.read()?.accessToken ?? null)
      : (options.accessToken ?? null);
    let response = await execute(initialToken);
    if (
      sessionBacked &&
      recoverAccessToken !== undefined &&
      isUnauthenticatedProblem(response.status, response.body)
    ) {
      const recovered = await recoverAccessToken(initialToken);
      if (recovered !== null) {
        response = await execute(recovered.accessToken);
      }
    }
    if (!response.ok) {
      throw new ApiError(response.status, response.body as Problem);
    }
    return response.body;
  };

  const requestBlob = async (path: string): Promise<DocumentContent> => {
    const execute = async (accessToken: string | null): Promise<Response> => {
      const headers = adminRequestHeaders(accessToken);
      return fetch(composeApiRequestUrl(baseUrl, path), {
        method: 'GET',
        credentials: 'include',
        headers,
      });
    };

    const initialToken = store.read()?.accessToken ?? null;
    let response = await execute(initialToken);
    if (recoverAccessToken !== undefined && response.status === 401) {
      let problem: Problem;
      try {
        problem = (await response.clone().json()) as Problem;
      } catch {
        problem = { code: 'REQUEST_FAILED' };
      }
      if (problem.code === 'UNAUTHENTICATED') {
        const recovered = await recoverAccessToken(initialToken);
        if (recovered !== null) {
          response = await execute(recovered.accessToken);
        } else {
          throw new ApiError(401, problem);
        }
      }
    }
    if (!response.ok) {
      let problem: Problem;
      try {
        problem = (await response.json()) as Problem;
      } catch {
        problem = { code: 'REQUEST_FAILED' };
      }
      throw new ApiError(response.status, problem);
    }
    const disposition = response.headers.get('Content-Disposition');
    return {
      blob: await response.blob(),
      filename: parseDocumentFilename(disposition),
    };
  };

  return {
    setAccessTokenRecoverer(recoverer: AdminAccessTokenRecoverer | undefined): void {
      recoverAccessToken = recoverer;
    },

    login: async (username: string, password: string): Promise<AdminSession> => {
      const body = (await request('POST', '/auth/login', {
        accessToken: null,
        body: {
          username,
          password,
          device: {
            installation_id: readWebInstallationId(),
            platform: 'WEB',
          },
        },
      })) as TokenPairBody;

      if (typeof body.access_token !== 'string') {
        throw new ApiError(500, { code: 'UNAUTHENTICATED' });
      }

      const session = await sessionFromAccessToken(request, body.access_token, body.auth_context);
      store.write(session);
      return session;
    },

    refreshFromCookie: async (): Promise<AdminSession> => {
      const body = (await request('POST', '/auth/refresh', {
        accessToken: null,
        body: {},
      })) as TokenPairBody;
      if (typeof body.access_token !== 'string') {
        throw new ApiError(500, { code: 'UNAUTHENTICATED' });
      }
      const session = await sessionFromAccessToken(request, body.access_token, body.auth_context);
      store.write(session);
      return session;
    },

    logout: async (): Promise<void> => {
      const session = store.read();
      if (session === null) {
        return;
      }
      await request('POST', '/auth/logout', { accessToken: session.accessToken });
    },

    changeOwnPassword: async (currentPassword: string, newPassword: string): Promise<void> => {
      await request('POST', '/auth/me/change-password', {
        body: { current_password: currentPassword, new_password: newPassword },
      });
    },

    listUsers: () => request('GET', '/users') as Promise<UserSummary[]>,
    listRoles: () => request('GET', '/roles') as Promise<{ code: string; name: string }[]>,
    getUser: (userId: string) => request('GET', `/users/${userId}`) as Promise<UserSummary>,
    onboardUser: (input: { username: string; displayName: string; roleCodes: readonly string[] }) =>
      request('POST', '/users/onboard-with-temporary-password', {
        body: {
          username: input.username,
          display_name: input.displayName,
          role_codes: input.roleCodes,
        },
      }) as Promise<TemporaryPasswordIssued>,
    issueTemporaryPassword: (userId: string, expectedUserVersion: string) =>
      request('POST', `/users/${userId}/temporary-password`, {
        body: { expected_user_version: expectedUserVersion },
      }) as Promise<TemporaryPasswordIssued>,
    replaceRoles: (userId: string, expectedUserVersion: string, roleCodes: readonly string[]) =>
      request('PUT', `/users/${userId}/roles`, {
        idempotency: true,
        body: { expected_user_version: expectedUserVersion, role_codes: roleCodes },
      }) as Promise<UserSummary>,
    setStatus: (userId: string, expectedUserVersion: string, status: 'ACTIVE' | 'DISABLED') =>
      request('POST', `/users/${userId}/set-status`, {
        idempotency: true,
        body: { expected_user_version: expectedUserVersion, status },
      }) as Promise<UserSummary>,
    getUserFacsimile: (userId: string) =>
      request('GET', `/users/${userId}/facsimile`) as Promise<AdminFacsimile>,
    provisionFacsimile: async (
      userId: string,
      expectedFacsimileVersion: string | null,
      file: File,
    ): Promise<unknown> => {
      const form = new FormData();
      form.set(
        'expected_facsimile_version',
        expectedFacsimileVersion === null ? 'null' : expectedFacsimileVersion,
      );
      form.set('file', file);
      return request('POST', `/users/${userId}/facsimile/versions`, { idempotency: true, form });
    },

    listContractorProfiles: () =>
      request('GET', '/admin/contractor-profiles') as Promise<ContractorProfile[]>,
    getContractorProfile: (id: string) =>
      request('GET', `/admin/contractor-profiles/${id}`) as Promise<ContractorProfile>,
    createContractorProfile: (body: unknown) =>
      request('POST', '/admin/contractor-profiles', {
        idempotency: true,
        body,
      }) as Promise<ContractorProfile>,
    updateContractorProfile: (id: string, body: unknown) =>
      request('PATCH', `/admin/contractor-profiles/${id}`, { body }) as Promise<ContractorProfile>,
    archiveContractorProfile: (id: string, expectedVersion: string) =>
      request('POST', `/admin/contractor-profiles/${id}/archive`, {
        idempotency: true,
        body: { expected_version: expectedVersion },
      }) as Promise<ContractorProfile>,
    contractorDuplicates: (profileId: string) =>
      request(
        'GET',
        `/admin/contractor-profiles/duplicate-candidates?profile_id=${encodeURIComponent(profileId)}`,
      ) as Promise<DuplicateCandidate<ContractorProfile>[]>,

    listVehicleProfiles: () =>
      request('GET', '/admin/vehicle-profiles') as Promise<VehicleProfile[]>,
    getVehicleProfile: (id: string) =>
      request('GET', `/admin/vehicle-profiles/${id}`) as Promise<VehicleProfile>,
    createVehicleProfile: (body: unknown) =>
      request('POST', '/admin/vehicle-profiles', {
        idempotency: true,
        body,
      }) as Promise<VehicleProfile>,
    updateVehicleProfile: (id: string, body: unknown) =>
      request('PATCH', `/admin/vehicle-profiles/${id}`, { body }) as Promise<VehicleProfile>,
    archiveVehicleProfile: (id: string, expectedVersion: string) =>
      request('POST', `/admin/vehicle-profiles/${id}/archive`, {
        idempotency: true,
        body: { expected_version: expectedVersion },
      }) as Promise<VehicleProfile>,
    vehicleDuplicates: (profileId: string) =>
      request(
        'GET',
        `/admin/vehicle-profiles/duplicate-candidates?profile_id=${encodeURIComponent(profileId)}`,
      ) as Promise<DuplicateCandidate<VehicleProfile>[]>,

    listContractorVehicleLinks: (contractorProfileId: string) =>
      request('GET', `/admin/contractor-profiles/${contractorProfileId}/vehicle-links`) as Promise<
        ContractorVehicleLink[]
      >,
    createContractorVehicleLink: (
      contractorProfileId: string,
      body: {
        readonly vehicle_profile_id: string;
        readonly expected_contractor_profile_version: string;
      },
    ) =>
      request('POST', `/admin/contractor-profiles/${contractorProfileId}/vehicle-links`, {
        idempotency: true,
        body,
      }) as Promise<ContractorVehicleLink>,
    archiveContractorVehicleLink: (
      contractorProfileId: string,
      linkId: string,
      body: { readonly expected_link_version: string },
    ) =>
      request(
        'POST',
        `/admin/contractor-profiles/${contractorProfileId}/vehicle-links/${linkId}/archive`,
        {
          idempotency: true,
          body,
        },
      ) as Promise<ContractorVehicleLink>,

    listChecklistTemplates: () =>
      request('GET', '/admin/checklist-templates') as Promise<ChecklistTemplateAdmin[]>,
    listDeletedChecklistTemplates: () =>
      request('GET', '/admin/checklist-templates?deleted=true') as Promise<ChecklistTemplateAdmin[]>,
    getChecklistTemplate: (id: string) =>
      request('GET', `/admin/checklist-templates/${id}`) as Promise<ChecklistTemplateAdmin>,
    createChecklistTemplate: (templateCode: string, name: string) =>
      request('POST', '/admin/checklist-templates', {
        idempotency: true,
        body: { template_code: templateCode, name },
      }) as Promise<ChecklistTemplateAdmin>,
    softDeleteChecklistTemplate: (
      id: string,
      body: { expected_version: string; deletion_note?: string | null },
    ) =>
      request('POST', `/admin/checklist-templates/${id}/soft-delete`, {
        idempotency: true,
        body,
      }) as Promise<ChecklistTemplateAdmin>,
    restoreChecklistTemplate: (id: string, body: { expected_version: string }) =>
      request('POST', `/admin/checklist-templates/${id}/restore`, {
        idempotency: true,
        body,
      }) as Promise<ChecklistTemplateAdmin>,
    listChecklistTemplateVersions: (id: string) =>
      request('GET', `/admin/checklist-templates/${id}/versions`) as Promise<
        {
          readonly id: string;
          readonly template_id?: string;
          readonly version_code: string;
          readonly status: string;
          readonly source_file: string;
          readonly source_checksum: string;
          readonly definition_checksum: string;
          readonly approved_at: string;
          readonly is_active: boolean;
        }[]
      >,
    assessChecklistTemplateActivationImpact: (
      checklistTemplateId: string,
      body: {
        expected_version: string;
        template_version_id: string | null;
        impact_mode: ConfigurationImpactMode;
      },
    ) =>
      request('POST', `/admin/checklist-templates/${checklistTemplateId}/activation-impact`, {
        idempotency: true,
        body,
      }) as Promise<ConfigurationImpactAssessment>,
    activateChecklistTemplateVersion: (
      checklistTemplateId: string,
      body: {
        expected_version: string;
        template_version_id: string | null;
        impact_assessment_id: string;
        impact_mode: ConfigurationImpactMode;
      },
    ) =>
      request('POST', `/admin/checklist-templates/${checklistTemplateId}/activate-version`, {
        idempotency: true,
        body,
      }) as Promise<ChecklistTemplateAdmin>,

    listImportProfiles: () =>
      request('GET', '/admin/template-import-profiles') as Promise<TemplateImportProfile[]>,
    createImportProfile: (code: string, sourceFamily: string) =>
      request('POST', '/admin/template-import-profiles', {
        idempotency: true,
        body: { code, source_family: sourceFamily },
      }) as Promise<TemplateImportProfile>,
    listImportRevisions: (profileId: string) =>
      request('GET', `/admin/template-import-profiles/${profileId}/revisions`) as Promise<{
        items: readonly {
          readonly id: string;
          readonly revision_no: number;
          readonly checksum: string;
          readonly is_current: boolean;
        }[];
      }>,
    getImportRevision: (profileId: string, revisionId: string) =>
      request(
        'GET',
        `/admin/template-import-profiles/${profileId}/revisions/${revisionId}`,
      ) as Promise<{
        readonly mapping: Record<string, unknown>;
        readonly header_binding_overlay: Record<string, unknown> | null;
      }>,
    createImportRevision: (
      profileId: string,
      expectedVersion: string,
      mapping: unknown,
      headerBindingOverlay?: unknown,
    ) =>
      request('POST', `/admin/template-import-profiles/${profileId}/revisions`, {
        idempotency: true,
        body: {
          expected_profile_version: expectedVersion,
          mapping,
          ...(headerBindingOverlay === undefined
            ? {}
            : { header_binding_overlay: headerBindingOverlay }),
        },
      }),
    uploadTemplateSource: async (file: File): Promise<TemplateSourceArtifact> => {
      const form = new FormData();
      form.set('file', file);
      return request('POST', '/admin/template-source-artifacts', {
        idempotency: true,
        form,
      }) as Promise<TemplateSourceArtifact>;
    },
    getTemplateSource: (id: string) =>
      request('GET', `/admin/template-source-artifacts/${id}`) as Promise<TemplateSourceArtifact>,
    createImportRun: (body: {
      source_artifact_id: string;
      source_sheet: string;
      import_profile_revision_id: string;
      checklist_template_id: string;
    }) =>
      request('POST', '/admin/template-import-runs', {
        idempotency: true,
        body,
      }) as Promise<TemplateImportRun>,
    getImportRun: (id: string) =>
      request('GET', `/admin/template-import-runs/${id}`) as Promise<TemplateImportRun>,
    correctImportRun: (id: string, expectedVersion: string, correction: unknown) =>
      request('POST', `/admin/template-import-runs/${id}/corrections`, {
        idempotency: true,
        body: { expected_version: expectedVersion, correction },
      }) as Promise<TemplateImportRun>,
    publishImportRun: (id: string, expectedVersion: string, versionCode: string) =>
      request('POST', `/admin/template-import-runs/${id}/publish`, {
        idempotency: true,
        body: { expected_version: expectedVersion, version_code: versionCode },
      }) as Promise<{
        import_run: TemplateImportRun;
        template_version: { id: string; version_code?: string; status?: string };
      }>,

    listTemplateAuthoringDrafts: (
      filters: {
        status?: TemplateAuthoringDraftStatus;
        checklistTemplateId?: string;
        provenance?: TemplateAuthoringProvenance;
      } = {},
    ) => {
      const params = new URLSearchParams();
      if (filters.status !== undefined) {
        params.set('status', filters.status);
      }
      if (filters.checklistTemplateId !== undefined) {
        params.set('checklist_template_id', filters.checklistTemplateId);
      }
      if (filters.provenance !== undefined) {
        params.set('provenance', filters.provenance);
      }
      const query = params.toString();
      return request(
        'GET',
        `/admin/template-authoring-drafts${query === '' ? '' : `?${query}`}`,
      ) as Promise<TemplateAuthoringDraftSummary[]>;
    },
    getTemplateAuthoringDraft: (draftId: string) =>
      request(
        'GET',
        `/admin/template-authoring-drafts/${draftId}`,
      ) as Promise<TemplateAuthoringDraft>,
    createTemplateAuthoringDraft: (checklistTemplateId: string, expectedTemplateVersion: string) =>
      request('POST', '/admin/template-authoring-drafts', {
        idempotency: true,
        body: {
          checklist_template_id: checklistTemplateId,
          expected_template_version: expectedTemplateVersion,
        },
      }) as Promise<TemplateAuthoringDraft>,
    createTemplateAuthoringDraftFromImportRun: (
      importRunId: string,
      expectedImportRunVersion: string,
    ) =>
      request('POST', '/admin/template-authoring-drafts/from-import-run', {
        idempotency: true,
        body: {
          import_run_id: importRunId,
          expected_import_run_version: expectedImportRunVersion,
        },
      }) as Promise<TemplateAuthoringDraft>,
    createTemplateAuthoringDraftFromTemplateVersion: (
      sourceTemplateVersionId: string,
      targetChecklistTemplateId: string,
      expectedTargetTemplateVersion: string,
    ) =>
      request('POST', '/admin/template-authoring-drafts/from-template-version', {
        idempotency: true,
        body: {
          source_template_version_id: sourceTemplateVersionId,
          target_checklist_template_id: targetChecklistTemplateId,
          expected_target_template_version: expectedTargetTemplateVersion,
        },
      }) as Promise<TemplateAuthoringDraft>,
    updateTemplateAuthoringDraft: (
      draftId: string,
      expectedVersion: string,
      authoringPayload: Record<string, unknown>,
    ) =>
      request('PATCH', `/admin/template-authoring-drafts/${draftId}`, {
        idempotency: true,
        body: {
          expected_version: expectedVersion,
          authoring_payload: authoringPayload,
        },
      }) as Promise<TemplateAuthoringDraft>,
    validateTemplateAuthoringDraft: (draftId: string, expectedVersion: string) =>
      request('POST', `/admin/template-authoring-drafts/${draftId}/validate`, {
        idempotency: true,
        body: { expected_version: expectedVersion },
      }) as Promise<TemplateAuthoringValidationReport>,
    previewTemplateAuthoringDraft: (draftId: string, view: TemplateAuthoringPreviewView) =>
      request(
        'GET',
        `/admin/template-authoring-drafts/${draftId}/preview?view=${encodeURIComponent(view)}`,
      ) as Promise<TemplateAuthoringPreview>,
    publishTemplateAuthoringDraft: (
      draftId: string,
      expectedVersion: string,
      versionCode: string,
    ) =>
      request('POST', `/admin/template-authoring-drafts/${draftId}/publish`, {
        idempotency: true,
        body: {
          expected_version: expectedVersion,
          version_code: versionCode,
        },
      }) as Promise<PublishedTemplateVersionResult>,
    discardTemplateAuthoringDraft: (draftId: string, expectedVersion: string) =>
      request('POST', `/admin/template-authoring-drafts/${draftId}/discard`, {
        idempotency: true,
        body: { expected_version: expectedVersion },
      }) as Promise<TemplateAuthoringDraft>,

    /**
     * Read-only CASE_DYNAMIC field definition catalog (ACTIVE published versions).
     * Not used by UI in this task; reserved for future «Добавить и связать».
     */
    listTemplateFieldReuseCandidates: (filters: {
      q?: string;
      /** Exact identity lookup; encoded as repeated `semantic_keys` query params. */
      semanticKeys?: readonly string[];
      excludeChecklistTemplateId?: string;
    } = {}) => {
      const params = new URLSearchParams();
      if (filters.q !== undefined) {
        params.set('q', filters.q);
      }
      if (filters.semanticKeys !== undefined) {
        for (const key of filters.semanticKeys) {
          params.append('semantic_keys', key);
        }
      }
      if (filters.excludeChecklistTemplateId !== undefined) {
        params.set('exclude_checklist_template_id', filters.excludeChecklistTemplateId);
      }
      const query = params.toString();
      return request(
        'GET',
        `/admin/template-field-reuse-candidates${query === '' ? '' : `?${query}`}`,
      ) as Promise<TemplateFieldReuseCandidate[]>;
    },

    listDocumentTypes: (filters: { status?: string; search?: string } = {}) => {
      const params = new URLSearchParams();
      if (filters.status !== undefined) {
        params.set('status', filters.status);
      }
      if (filters.search !== undefined && filters.search.length > 0) {
        params.set('search', filters.search);
      }
      const query = params.toString();
      return request('GET', `/admin/document-types${query === '' ? '' : `?${query}`}`) as Promise<
        DocumentTypeAdmin[]
      >;
    },
    getDocumentType: (documentTypeId: string) =>
      request('GET', `/admin/document-types/${documentTypeId}`) as Promise<DocumentTypeAdmin>,
    createDocumentType: (body: {
      code: string;
      display_name: string;
      default_metadata_schema: Record<string, unknown>;
      description?: string | null;
    }) =>
      request('POST', '/admin/document-types', {
        idempotency: true,
        body,
      }) as Promise<DocumentTypeAdmin>,
    updateDocumentType: (
      documentTypeId: string,
      body: {
        expected_version: string;
        display_name?: string;
        description?: string | null;
        default_metadata_schema?: Record<string, unknown>;
      },
    ) =>
      request('PATCH', `/admin/document-types/${documentTypeId}`, {
        idempotency: true,
        body,
      }) as Promise<DocumentTypeAdmin>,
    archiveDocumentType: (documentTypeId: string, expectedVersion: string) =>
      request('POST', `/admin/document-types/${documentTypeId}/archive`, {
        idempotency: true,
        body: { expected_version: expectedVersion },
      }) as Promise<DocumentTypeAdmin>,

    listEquipmentTypes: () =>
      request('GET', '/admin/equipment-types') as Promise<EquipmentTypeAdmin[]>,
    getEquipmentType: (equipmentTypeId: string) =>
      request('GET', `/admin/equipment-types/${equipmentTypeId}`) as Promise<EquipmentTypeAdmin>,
    createEquipmentType: (body: {
      code: string;
      display_name: string;
      display_order: number;
      description?: string | null;
    }) =>
      request('POST', '/admin/equipment-types', {
        idempotency: true,
        body,
      }) as Promise<EquipmentTypeAdmin>,
    updateEquipmentType: (
      equipmentTypeId: string,
      body: {
        expected_version: string;
        display_name?: string;
        description?: string | null;
        display_order?: number;
      },
    ) =>
      request('PATCH', `/admin/equipment-types/${equipmentTypeId}`, {
        idempotency: true,
        body,
      }) as Promise<EquipmentTypeAdmin>,
    archiveEquipmentType: (equipmentTypeId: string, expectedVersion: string) =>
      request('POST', `/admin/equipment-types/${equipmentTypeId}/archive`, {
        idempotency: true,
        body: { expected_version: expectedVersion },
      }) as Promise<EquipmentTypeAdmin>,
    listEquipmentPackageRevisions: (equipmentTypeId: string) =>
      request('GET', `/admin/equipment-types/${equipmentTypeId}/package-revisions`) as Promise<
        EquipmentPackageRevision[]
      >,
    getEquipmentPackageRevision: (equipmentTypeId: string, packageRevisionId: string) =>
      request(
        'GET',
        `/admin/equipment-types/${equipmentTypeId}/package-revisions/${packageRevisionId}`,
      ) as Promise<EquipmentPackageRevision>,
    createEquipmentPackageRevision: (
      equipmentTypeId: string,
      body: {
        expected_equipment_type_version: string;
        items: readonly { checklist_template_id: string; ordinal: number }[];
      },
    ) =>
      request('POST', `/admin/equipment-types/${equipmentTypeId}/package-revisions`, {
        idempotency: true,
        body,
      }) as Promise<EquipmentPackageRevision>,
    assessEquipmentPackageActivationImpact: (
      equipmentTypeId: string,
      packageRevisionId: string,
      body: {
        expected_equipment_type_version: string;
        impact_mode: ConfigurationImpactMode;
      },
    ) =>
      request(
        'POST',
        `/admin/equipment-types/${equipmentTypeId}/package-revisions/${packageRevisionId}/activation-impact`,
        { idempotency: true, body },
      ) as Promise<ConfigurationImpactAssessment>,
    activateEquipmentPackageRevision: (
      equipmentTypeId: string,
      packageRevisionId: string,
      body: {
        expected_equipment_type_version: string;
        impact_assessment_id: string;
        impact_mode: ConfigurationImpactMode;
      },
    ) =>
      request(
        'POST',
        `/admin/equipment-types/${equipmentTypeId}/package-revisions/${packageRevisionId}/activate`,
        { idempotency: true, body },
      ) as Promise<EquipmentTypeAdmin>,

    listInspectionSites: () =>
      request('GET', '/admin/inspection-sites') as Promise<InspectionSiteAdmin[]>,
    getInspectionSite: (inspectionSiteId: string) =>
      request('GET', `/admin/inspection-sites/${inspectionSiteId}`) as Promise<InspectionSiteAdmin>,
    createInspectionSite: (body: {
      code: string;
      display_name: string;
      display_order: number;
      description?: string | null;
    }) =>
      request('POST', '/admin/inspection-sites', {
        idempotency: true,
        body,
      }) as Promise<InspectionSiteAdmin>,
    updateInspectionSite: (
      inspectionSiteId: string,
      body: {
        expected_version: string;
        display_name?: string;
        description?: string | null;
        display_order?: number;
      },
    ) =>
      request('PATCH', `/admin/inspection-sites/${inspectionSiteId}`, {
        idempotency: true,
        body,
      }) as Promise<InspectionSiteAdmin>,
    archiveInspectionSite: (inspectionSiteId: string, expectedVersion: string) =>
      request('POST', `/admin/inspection-sites/${inspectionSiteId}/archive`, {
        idempotency: true,
        body: { expected_version: expectedVersion },
      }) as Promise<InspectionSiteAdmin>,

    listQualifications: () =>
      request('GET', '/admin/inspector-template-qualifications') as Promise<
        InspectorQualification[]
      >,
    putQualification: (body: {
      user_id: string;
      checklist_template_id: string;
      status: 'ACTIVE' | 'INACTIVE';
      expected_version: string | null;
    }) =>
      request('PUT', '/admin/inspector-template-qualifications', {
        idempotency: true,
        body,
      }) as Promise<InspectorQualification>,
    listInspectionCases: (status?: string) => {
      const query =
        status === undefined || status.length === 0 ? '' : `?status=${encodeURIComponent(status)}`;
      return request('GET', `/inspection-cases${query}`) as Promise<InspectionCaseSummary[]>;
    },
    getInspectionCase: (caseId: string) =>
      request('GET', `/inspection-cases/${caseId}`) as Promise<InspectionCaseDetail>,
    getChecklistInstance: (checklistId: string) =>
      request('GET', `/checklist-instances/${checklistId}`) as Promise<ChecklistInstanceDetail>,
    getTemplateVersion: (templateVersionId: string) =>
      request(
        'GET',
        `/template-versions/${templateVersionId}`,
      ) as Promise<PublishedTemplateVersionDetail>,
    getInspectionDecision: (caseId: string) =>
      request('GET', `/inspection-cases/${caseId}/decision`) as Promise<InspectionDecision>,
    getInspectionResultVersion: (caseId: string) =>
      request(
        'GET',
        `/inspection-cases/${caseId}/result-version`,
      ) as Promise<InspectionResultVersion>,
    getSignatureRecord: (signatureId: string) =>
      request('GET', `/signature-records/${signatureId}`) as Promise<SignatureRecord>,
    getDocumentArtifact: (artifactId: string) =>
      request('GET', `/document-artifacts/${artifactId}`) as Promise<DocumentArtifact>,
    downloadDocumentContent: (artifactId: string) =>
      requestBlob(`/document-artifacts/${artifactId}/content`),
    retryPdfGeneration: (artifactId: string, expectedDocumentArtifactVersion: string) =>
      request('POST', `/document-artifacts/${artifactId}/retry-generation`, {
        idempotency: true,
        body: { expected_document_artifact_version: expectedDocumentArtifactVersion },
      }) as Promise<DocumentArtifact>,
    listCaseEmailDeliveries: (caseId: string) =>
      request('GET', `/inspection-cases/${caseId}/email-deliveries`) as Promise<EmailDelivery[]>,
    getEmailDelivery: (deliveryId: string) =>
      request('GET', `/email-deliveries/${deliveryId}`) as Promise<EmailDelivery>,
    retryEmailDelivery: (deliveryId: string, expectedEmailDeliveryVersion: string) =>
      request('POST', `/email-deliveries/${deliveryId}/retry`, {
        idempotency: true,
        body: { expected_email_delivery_version: expectedEmailDeliveryVersion },
      }) as Promise<EmailDelivery>,
    searchArchive: (
      filters: {
        caseNumber?: string;
        vehicleRegistrationNumber?: string;
        decision?: string;
      } = {},
    ) => {
      const params = new URLSearchParams();
      if (filters.caseNumber !== undefined && filters.caseNumber.length > 0) {
        params.set('case_number', filters.caseNumber);
      }
      if (
        filters.vehicleRegistrationNumber !== undefined &&
        filters.vehicleRegistrationNumber.length > 0
      ) {
        params.set('vehicle_registration_number', filters.vehicleRegistrationNumber);
      }
      if (filters.decision !== undefined && filters.decision.length > 0) {
        params.set('decision', filters.decision);
      }
      const query = params.toString();
      return request(
        'GET',
        `/archive/inspection-cases${query === '' ? '' : `?${query}`}`,
      ) as Promise<ArchivedInspectionCaseSummary[]>;
    },
    getArchivedCaseBundle: (caseId: string) =>
      request('GET', `/archive/inspection-cases/${caseId}`) as Promise<ArchivedCaseBundle>,
    getCaseAudit: (caseId: string) =>
      request('GET', `/inspection-cases/${caseId}/audit-events`) as Promise<AdminAuditEvent[]>,
    getInspectorAccessPolicy: () =>
      request('GET', '/admin/inspector-access-policy') as Promise<{
        simplified_inspector_access: boolean;
      }>,
    putInspectorAccessPolicy: (simplifiedInspectorAccess: boolean) =>
      request('PUT', '/admin/inspector-access-policy', {
        body: { simplified_inspector_access: simplifiedInspectorAccess },
      }) as Promise<{ simplified_inspector_access: boolean }>,
    putChecklistInspectorAssignment: (
      caseId: string,
      checklistInstanceId: string,
      expectedCaseVersion: string,
      userId: string | null,
    ) =>
      request(
        'PUT',
        `/admin/inspection-cases/${caseId}/checklist-instances/${checklistInstanceId}/inspector-assignment`,
        {
          idempotency: true,
          body: { expected_case_version: expectedCaseVersion, user_id: userId },
        },
      ),

    listReferenceDatasets: () =>
      request('GET', '/admin/reference-data') as Promise<{
        items: readonly ReferenceDatasetSummary[];
      }>,
    getReferenceDataset: (datasetKey: string) =>
      request(
        'GET',
        `/admin/reference-data/${encodeURIComponent(datasetKey)}`,
      ) as Promise<ReferenceDataset>,
    replaceReferenceDataset: (
      datasetKey: string,
      expectedVersion: string,
      payload: Record<string, unknown>,
    ) =>
      request('PUT', `/admin/reference-data/${encodeURIComponent(datasetKey)}`, {
        body: { expected_version: expectedVersion, payload },
      }) as Promise<ReferenceDataset>,

    listEmailPolicies: () =>
      request('GET', '/admin/email-recipient-policies') as Promise<EmailRecipientPolicy[]>,
    getEmailPolicy: (policyId: string) =>
      request(
        'GET',
        `/admin/email-recipient-policies/${policyId}`,
      ) as Promise<EmailRecipientPolicy>,
    createEmailPolicy: (body: unknown) =>
      request('POST', '/admin/email-recipient-policies', {
        idempotency: true,
        body,
      }) as Promise<EmailRecipientPolicy>,
    updateEmailPolicy: (policyId: string, body: unknown) =>
      request('PATCH', `/admin/email-recipient-policies/${policyId}`, {
        body,
      }) as Promise<EmailRecipientPolicy>,
    previewEmailResolution: (body: unknown) =>
      request('POST', '/admin/email-recipient-policies/resolve-preview', {
        body,
      }) as Promise<EmailRecipientResolution>,
    previewCaseEmailRecipients: (body: { inspection_case_id: string }) =>
      request('POST', '/admin/email-recipient-policies/resolve-case-preview', {
        body,
      }) as Promise<EmailCaseRecipientPreview>,

    getRubitimeConfig: () =>
      request('GET', '/admin/integrations/rubitime/config') as Promise<RubitimeBusinessConfig>,
    replaceRubitimeConfig: (body: {
      expected_version: string;
      branch_id: string;
      service_id: string;
      cooperator_id: string;
    }) =>
      request('PUT', '/admin/integrations/rubitime/config', {
        body,
      }) as Promise<RubitimeBusinessConfig>,

    getOpsSummary: () => request('GET', '/admin/ops-summary') as Promise<AdminOpsSummary>,
    searchAuditEvents: (filters: { eventType?: string; inspectionCaseId?: string } = {}) => {
      const params = new URLSearchParams();
      if (filters.eventType) {
        params.set('event_type', filters.eventType);
      }
      if (filters.inspectionCaseId) {
        params.set('inspection_case_id', filters.inspectionCaseId);
      }
      const query = params.toString();
      return request('GET', `/admin/audit-events${query === '' ? '' : `?${query}`}`) as Promise<
        AdminAuditEvent[]
      >;
    },
  };
}

export type AdminApi = ReturnType<typeof createAdminApi>;
